export type FeatureMetric = { value: string; label: string };
export type FeatureStep = { title: string; text: string };
export type FeatureRoleTab = {
  role: string;
  items: { title: string; text: string }[];
};
export type FeatureTestimonial = { quote: string; name: string; role: string };

export type FeatureExtra = {
  metrics: FeatureMetric[];
  stepsTitle: string;
  stepsLead: string;
  steps: FeatureStep[];
  roles: FeatureRoleTab[];
  included: string[];
  capabilities?: { title: string; text: string; plan: "Standard" | "Pro" | "Advanced" | "Enterprise" }[];
  testimonialsTitle: string;
  testimonials: FeatureTestimonial[];
  closingTitle: string;
};

export const FEATURE_EXTRAS: Record<string, FeatureExtra> = {
  sales: {
    metrics: [
      { value: "-20%", label: "Order processing time" },
      { value: "+20%", label: "Repeat sales thanks to on-time delivery" },
      { value: "-30%", label: "Number of errors and returns" },
    ],
    stepsTitle: "Orders that work on their own",
    stepsLead:
      "Every stage from customer request to shipment — automated and under control.",
    steps: [
      {
        title: "Order creation",
        text: "The manager creates an order based on a request or quotation.",
      },
      {
        title: "Stock check",
        text: "The system automatically displays product availability and reserves batches.",
      },
      {
        title: "Status control",
        text: "You always know whether an order is confirmed or in progress.",
      },
      {
        title: "Shipment and documentation",
        text: "The system generates delivery notes and records fulfillment.",
      },
    ],
    roles: [
      {
        role: "Business Owner / CEO",
        items: [
          {
            title: "Transparent sales overview",
            text: "Sees order volumes, fulfillment, and projected revenue in real time.",
          },
          {
            title: "Fulfilling customer commitments",
            text: "Assurance that delivery deadlines are tracked and met.",
          },
          {
            title: "Increased customer trust",
            text: "Fewer disruptions, more repeat orders.",
          },
        ],
      },
      {
        role: "Head of Production (COO)",
        items: [
          {
            title: "Synchronization of production and sales",
            text: "Orders are instantly transformed into production tasks.",
          },
          {
            title: "Less chaos",
            text: "Automatic status tracking — from creation to shipment.",
          },
          {
            title: "Flexible planning",
            text: "Real-time stock availability check helps prevent shortages.",
          },
        ],
      },
      {
        role: "Chief Financial Officer (CFO)",
        items: [
          {
            title: "Transparent revenue tracking",
            text: "Accurate shipping and returns data.",
          },
          {
            title: "Accounts receivable control",
            text: "All orders are tracked until financial documents are closed.",
          },
          {
            title: "Improved cash flow",
            text: "Thanks to fast invoice and delivery note generation.",
          },
        ],
      },
      {
        role: "Sales Manager",
        items: [
          {
            title: "Speed",
            text: "See what's in stock in real time and promise customers accurate delivery dates.",
          },
          {
            title: "Less busywork",
            text: "Automatic generation of delivery and return documents.",
          },
          {
            title: "Full order visibility",
            text: "All customer orders in one place, with statuses updated automatically.",
          },
        ],
      },
    ],
    included: [
      "Creation and management of sales orders.",
      "Creation of quotations and converting them into orders.",
      "Automatic tracking of order statuses (new, in progress, shipped, completed).",
      "Monitoring fulfillment and delivery deadlines.",
      "Real-time product availability check.",
      "Stock reservation for sales order.",
      "Generating shipment and return documents.",
      "Automatic data updates after issuing delivery documents.",
      "Full transparency of customer order fulfillment.",
    ],
    capabilities: [
      { title: "A promised date that holds", text: "Sales sees production progress on the order line instead of chasing the shop floor.", plan: "Standard" },
      { title: "Delivery as its own document", text: "The warehouse knows what to pick while sales sees exactly where fulfilment stands.", plan: "Pro" },
      { title: "Delivery schedules by order line", text: "Commit quantities in phases instead of promising everything by month-end.", plan: "Pro" },
      { title: "Currencies without spreadsheet rework", text: "Quote and issue goods in the deal currency without manual recalculation.", plan: "Standard" },
      { title: "Commercial data stays private", text: "Dealers, regions and business lines see only the orders and prices they should.", plan: "Advanced" },
      { title: "Start production from an approved quote", text: "Reserve capacity and materials before a conventional sales order slows you down.", plan: "Pro" },
    ],
    testimonialsTitle: "Trusted with their sales",
    testimonials: [
      {
        quote:
          "Thanks to automatic reservation, we no longer confirm to customers products that aren't actually in stock.",
        name: "Ahmed K.",
        role: "Head of Sales",
      },
      {
        quote:
          "I can see all orders in one system and track customer shipments daily.",
        name: "Omar Al Hashimi",
        role: "Sales Manager",
      },
      {
        quote:
          "Less time is spent preparing shipping documents — the system generates them automatically.",
        name: "Huda Al Mansoori",
        role: "Sales Coordinator",
      },
      {
        quote:
          "Now I can see all orders in one place and instantly check stock before confirming with the customer. What used to take hours now takes minutes.",
        name: "Rohit M.",
        role: "Sales Manager",
      },
    ],
    closingTitle: "Feel how effortless sales management can be — try factory.online",
  },
  production: {
    metrics: [
      { value: "-25%", label: "Production delays" },
      { value: "+12%", label: "Work center productivity" },
      { value: "-25%", label: "Time spent on manual planning" },
    ],
    stepsTitle: "Production without chaos",
    stepsLead:
      "The system plans resources automatically — you just oversee timing and performance.",
    steps: [
      {
        title: "Order creation",
        text: "A job order is created with all parameters defined.",
      },
      {
        title: "Requirements planning",
        text: "The system determines required materials, capacities, and resources.",
      },
      {
        title: "Order execution",
        text: "Work centers receive specific tasks with deadlines.",
      },
      {
        title: "Performance monitoring",
        text: "Statuses update automatically — changes are visible instantly.",
      },
    ],
    roles: [
      {
        role: "Business Owner / CEO",
        items: [
          {
            title: "Transparency",
            text: "A clear view of all production orders — what's being made, when, and in what quantity.",
          },
          {
            title: "Due dates control",
            text: "Fewer delivery disruptions and on-time contract fulfillment.",
          },
          {
            title: "Agility",
            text: "Fast response to changes in customer orders or production priorities.",
          },
        ],
      },
      {
        role: "Chief Operating Officer (COO)",
        items: [
          {
            title: "Full control",
            text: "Manufacturing orders are created based on customer orders (MTO) or warehouse needs (MTS).",
          },
          {
            title: "Status control",
            text: 'The system automatically updates stages — "in progress," "approved," "done."',
          },
          {
            title: "Change tracking",
            text: "Automatic tracking of order or product changes — no chaos.",
          },
        ],
      },
      {
        role: "Chief Financial Officer (CFO)",
        items: [
          {
            title: "Transparent budgets",
            text: "Accurate calculation of material, resources and labor requirements.",
          },
          {
            title: "Less losses",
            text: "Reduced risk of overproduction and frozen capital in inventory.",
          },
          {
            title: "Actual cost tracking",
            text: "Cost control per manufacturing order.",
          },
        ],
      },
    ],
    included: [
      "Create manufacturing orders based on sales orders or warehouse needs.",
      "Define requirements for batches, marking, and packaging",
      "Material and semi-finished goods requirements planning.",
      "Production capacity requirements (MRPII, MRP) planning.",
      "Manage manufacturing order statuses.",
      "Due dates monitoring and changes tracking in manufacturing orders.",
      "Recording of finished goods output and defects with actual operation times.",
      "Finished goods receipt to warehouse — to a specific order or free stock.",
      "Automatic commitment of work-in-progress for manufacturing orders.",
      "Quantity tolerance handling during receiving and shipping.",
      "Synchronization of manufacturing orders with up-to-date BOM and routing versions.",
      "Change management: choose whether to continue with the old version or update using the new one.",
    ],
    capabilities: [
      { title: "Approvals leave the messenger", text: "Required documents follow a visible approval path instead of verbal agreements.", plan: "Pro" },
      { title: "Every change has an owner", text: "See who changed a document or setting and where the action came from.", plan: "Advanced" },
      { title: "Files and discussion stay with the work", text: "Keep decisions beside the order or batch instead of losing context in email.", plan: "Standard" },
      { title: "Each location sees its own work", text: "Distributed warehouses operate with access tailored to their location.", plan: "Advanced" },
      { title: "Faster shop-floor to warehouse reconciliation", text: "Group related finished-goods receipts and review their lines together.", plan: "Pro" },
    ],
    testimonialsTitle: "Trusted with their production",
    testimonials: [
      {
        quote:
          "The system instantly shows if resources are available to fulfill an order, helping avoid overloads.",
        name: "Liam B.",
        role: "Production Planner",
      },
      {
        quote:
          "We can now better forecast equipment usage and prevent downtime.",
        name: "Noah W.",
        role: "Operations Manager",
      },
      {
        quote:
          "I have real-time status for every order and can respond quickly to any deviations.",
        name: "Oliver S.",
        role: "Production Supervisor",
      },
      {
        quote:
          "Customer order changes no longer create chaos — the system updates the plan automatically.",
        name: "James W.",
        role: "Production Supervisor",
      },
    ],
    closingTitle: "Take the chaos out of production — start factory.online today",
  },
  purchases: {
    metrics: [
      { value: "-18%", label: "Delayed deliveries" },
      { value: "-12%", label: "Procurement costs thanks to transparent control" },
      { value: "-40%", label: "Time spent processing requests and orders" },
    ],
    stepsTitle: "Procurement without delays",
    stepsLead:
      "From request to material arrival — full real-time transparency.",
    steps: [
      {
        title: "Purchase request (PR) creation",
        text: "The team defines material requirements.",
      },
      {
        title: "Transformation to purchase order (PO)",
        text: "The system generates the purchase order automatically.",
      },
      {
        title: "Status monitoring",
        text: "See whether items are pending, partially, or fully received.",
      },
      {
        title: "Receipt and updates",
        text: "GRN automatically updates status and inventory levels.",
      },
    ],
    roles: [
      {
        role: "Head of Procurement",
        items: [
          {
            title: "Full-cycle procurement transparency",
            text: "From purchase request (PR) to goods receipt (GRN).",
          },
          {
            title: "Automatic order status updates",
            text: "As a result — less manual work and fewer errors.",
          },
          {
            title: "Delivery due dates control",
            text: "And minimized risk of production delays.",
          },
        ],
      },
      {
        role: "Chief Financial Officer / Accountant",
        items: [
          {
            title: "Unified document flow",
            text: "PR → PO → GRN → Invoice — simplifying cost control.",
          },
          {
            title: "Less duplication and fewer errors",
            text: "Purchases are automatically reflected in financial accounting.",
          },
          {
            title: "Easy budget control",
            text: "For purchases, actual expenses, and deviations.",
          },
        ],
      },
      {
        role: "Business owner / CEO",
        items: [
          {
            title: "Timely purchasing",
            text: "Ensuring smooth, continuous production.",
          },
          {
            title: "Real-time supply chain visibility",
            text: "Know what's ordered, what's received, and where the risks are.",
          },
          {
            title: "Ability to make strategic decisions",
            text: "Based on data, not assumptions.",
          },
        ],
      },
    ],
    included: [
      "Creation and management of purchase requisition.",
      "Converting requisition into supplier purchase orders",
      "Monitoring purchase order statuses (pending, partially received, fully received).",
      "Automatic status updates based on goods receipt documents.",
      "Control over delivery due dates and on-time performance.",
      "Forecasting material requirements for procurement.",
      "Managing safety stock levels and lead times.",
      "Generating purchase requisition based on shortage forecasts.",
      "Selecting and fine-tuning the procurement strategy for each material: procurement based on safety stock levels, strictly based on actual demand, using the reorder point model, or on a fixed schedule to minimize tied-up inventory.",
    ],
    capabilities: [
      { title: "Requisitions become supplier orders", text: "Consolidate demand without retyping lines or losing their source.", plan: "Standard" },
      { title: "Supplier dates feed the plan", text: "Expected deliveries become visible to material planning and shortage control.", plan: "Pro" },
      { title: "Services count alongside materials", text: "Subcontracted work and purchased services stay visible in procurement cost.", plan: "Pro" },
    ],
    testimonialsTitle: "Trusted with their procurement",
    testimonials: [
      {
        quote:
          "I can see the status of all requisitions and orders in real time, which helps me keep suppliers under control.",
        name: "Liam B.",
        role: "Procurement Coordinator",
      },
      {
        quote:
          "We used to often confuse which orders were already fulfilled. Now statuses update automatically after receipt.",
        name: "Noah W.",
        role: "Purchasing Officer",
      },
      {
        quote:
          "The system automatically generates purchase requisitions, so we've stopped managing them manually in Excel. It brought transparency and reduced the risk of duplicate orders.",
        name: "Oliver S.",
        role: "Procurement Head",
      },
      {
        quote:
          "Requests instantly transform into purchase orders — cutting down processing time dramatically.",
        name: "James W.",
        role: "Procurement Head",
      },
    ],
    closingTitle: "Make your procurement predictable with factory.online",
  },
  stocks: {
    metrics: [
      { value: "-25%", label: "Less frozen stock" },
      { value: "-50%", label: "Stock-taking mismatches" },
      { value: "99+%", label: "Real-time visibility of actual stock levels" },
    ],
    stepsTitle: "Inventory always under control",
    stepsLead:
      "No blind spots — every batch and stock level is traceable in real time.",
    steps: [
      {
        title: "Material receipts",
        text: "Batches created with QR codes and expiration dates.",
      },
      {
        title: "Stock updates",
        text: "Synchronized data across all warehouses.",
      },
      {
        title: "Stock-taking",
        text: "Mobile app for fast physical inventory checks.",
      },
      {
        title: "Track & Trace",
        text: "Every batch tracked to final usage.",
      },
    ],
    roles: [
      {
        role: "Supply & Logistics Managers",
        items: [
          {
            title: "Transparent control of supply",
            text: "GRN instantly updates PO statuses — a clear view of what's received and what's still on the way.",
          },
          {
            title: "Full batch traceability",
            text: "QR codes, expiration dates, complete tracking of materials and products.",
          },
          {
            title: "Online stock monitoring",
            text: "Real-time visibility across all storage locations.",
          },
        ],
      },
      {
        role: "Chief Financial Officer (CFO)",
        items: [
          {
            title: "Single source of truth",
            text: "Inventory and movement data are always accurate and updated automatically.",
          },
          {
            title: "Transparent expense tracking",
            text: "GRNs confirm actual receipts with minimal manual errors.",
          },
          {
            title: "Reduced tied-up capital",
            text: "MRP helps balance purchasing and minimize excess stock.",
          },
        ],
      },
      {
        role: "Business Owner / CEO",
        items: [
          {
            title: "Production continuity",
            text: "The system ensures required materials are always available.",
          },
          {
            title: "Balanced resources",
            text: 'Sufficient stock to keep operations running without "dead weight."',
          },
          {
            title: "Risk protection",
            text: "Batch traceability reduces losses from defects, expiration, or theft.",
          },
        ],
      },
    ],
    included: [
      "Material and product receipts with automatic GRN creation",
      "Link to POs for fulfillment tracking",
      "Automatic batch creation with QR codes and shelf-life control",
      "QR code printing for physical labeling",
      "Real-time stock monitoring across multiple locations",
      "Batch-level item traceability",
      "Manual adjustments after stocktakes or discrepancies",
      "Supply vs demand comparison for shortage forecasting",
      "Automatic PR/PO generation and supplier orders",
      "Inventory movements across locations with batch traceability",
      "Stock management strategies: FIFO, LIFO, FEFO (expiry date based) and ABC/XYZ classification for priority control.",
    ],
    capabilities: [
      { title: "Quarantine before consumption", text: "A batch under inspection cannot reach production before quality releases it.", plan: "Pro" },
      { title: "Trace every batch both ways", text: "Find the source of a defect and every finished product or customer it affects.", plan: "Advanced" },
      { title: "Use the right material batch", text: "Batch attributes keep variable raw materials and customer requirements under control.", plan: "Advanced" },
      { title: "Packaging and containers stay visible", text: "The physical form of stock matches its digital record at receipt and issue.", plan: "Pro" },
      { title: "Scan it instead of typing it", text: "Find stock and print labels faster with fewer warehouse mistakes.", plan: "Standard" },
      { title: "Stop negative stock before posting", text: "Users see the risk before inventory records become distorted.", plan: "Standard" },
      { title: "Count stock on the real business date", text: "Post controlled adjustments into the period where the movement belongs.", plan: "Pro" },
    ],
    testimonialsTitle: "Trusted with their inventory",
    testimonials: [
      {
        quote:
          "I find needed batches much faster because everything is labeled and traceable.",
        name: "Liam B.",
        role: "Warehouse Supervisor",
      },
      {
        quote:
          "We manage expiration dates better and write off fewer materials.",
        name: "Noah W.",
        role: "Warehouse Manager",
      },
      {
        quote:
          "Stock levels are visible in real time — this reduced unnecessary material orders.",
        name: "Oliver S.",
        role: "Warehouse Manager",
      },
      {
        quote:
          "QR coding and automatic stock updates gave me an accurate picture of our warehouses. We reduced discrepancies after stocktakes and now find the right batches much faster.",
        name: "James W.",
        role: "Inventory Controller",
      },
    ],
    closingTitle: "Bring order to your warehouses with factory.online",
  },
  planning: {
    metrics: [
      { value: "+20%", label: "Improved capacity thanks to early detection of bottlenecks" },
      { value: "-30%", label: "Fewer material shortages through MRP and safety stock management" },
      { value: "+25%", label: "More accurate forecasts with scenario modeling" },
    ],
    stepsTitle: "See ahead — act with confidence",
    stepsLead:
      "The system shows capacity loads and material needs before problems arise, helping you choose the optimal scenario.",
    steps: [
      {
        title: "Rough-Cut Planning",
        text: "Resource and material requirements calculated by days/weeks/months.",
      },
      {
        title: "Dependent demand analysis",
        text: "Identification of bottlenecks and critical materials.",
      },
      {
        title: "Scenario modeling",
        text: "Parallel calculations for different what-if cases.",
      },
      {
        title: "Simulation forecasting",
        text: "Assessing the impact of changes on throughput.",
      },
    ],
    roles: [
      {
        role: "Production Manager",
        items: [
          {
            title: "Resource load forecasting",
            text: "See capacity utilization days, weeks, or months in advance.",
          },
          {
            title: "Early bottleneck detection",
            text: "Spot critical resources and materials at the planning stage, not in crisis.",
          },
          {
            title: "Scenario analysis for better decisions",
            text: "Compare alternative plans and pick the best one.",
          },
        ],
      },
      {
        role: "Planner",
        items: [
          {
            title: "Automatic requirements planning (MRP)",
            text: "The system automatically determines required materials and components.",
          },
          {
            title: "Resource shortage analysis",
            text: "The planner sees what is missing now and what will become critical in the future.",
          },
          {
            title: "Simulation-based planning",
            text: "Simultaneous calculation of multiple scenarios to choose the best option.",
          },
        ],
      },
      {
        role: "Chief Operations Officer (COO)",
        items: [
          {
            title: "Realistic view of capacity",
            text: "Clear visibility into whether production can handle future demand.",
          },
          {
            title: "Lower risk of order delays",
            text: "Bottlenecks are eliminated in advance through planning.",
          },
          {
            title: "Strategic efficiency management",
            text: "Planning data becomes the foundation for company-level decisions.",
          },
        ],
      },
    ],
    included: [
      "Rough-Cut Capacity Planning (RCCP)",
      "Workforce requirements planning with constrained/unconstrained availability models",
      "Material planning (MRP) with supply and safety stock consideration",
      "Automatic detection of bottlenecks and critical materials",
      "Scenario modeling for simulation analysis and decision-making",
      "Parallel calculations for multiple scenarios",
      "Calendars for shifts, holidays, and maintenance schedules",
      "Throughput forecasting for production",
      "Visualization of resource load by days, weeks, months",
    ],
    capabilities: [
      { title: "Batch rules that match the shop floor", text: "Split demand by batch, container or production multiple instead of forcing one planning rule.", plan: "Advanced" },
      { title: "Compare plans before releasing one", text: "Keep schedule versions, review the impact and release the plan the team agreed on.", plan: "Advanced" },
    ],
    testimonialsTitle: "Trusted with their planning",
    testimonials: [
      {
        quote:
          "Scenario modeling gave us the ability to test different plan versions. It really saves resources.",
        name: "Liam B.",
        role: "Planner",
      },
      {
        quote:
          "For me, the key is confidence that orders will be fulfilled. The system provides a transparent picture of capacity load.",
        name: "Noah W.",
        role: "COO",
      },
      {
        quote:
          "Before, we only saw problems when they turned into crises. Now bottlenecks are visible right in the plan.",
        name: "Oliver S.",
        role: "COO",
      },
      {
        quote:
          "Before, we only saw problems once they turned into a crisis. Now bottlenecks are visible right in the plan.",
        name: "James W.",
        role: "COO",
      },
    ],
    closingTitle:
      "Detect and eliminate bottlenecks at the planning stage — start rough-cut planning in your production today with factory.online",
  },
};
